import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import Box from "ds/components/Box";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { pluralize } from "shared/Pluralize";
import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields, AssignRoleFormEditFields } from "components/AssignRoleForm/types";
import { CREATE_FORM_ID } from "components/AssignRoleForm/constants";

import {
  USER_ROLE_BINDING_BATCH_CREATE,
  USER_ROLE_BINDING_UPDATE,
  USER_ROLE_BINDINGS_WITH_ROLES,
} from "./gql";
import { showUnassignConfirmation } from "../Roles/UnassignConfirmation";

type AssignRoleDrawerProps = {
  userId: string;
};

const AssignRoleDrawer = createDrawer(({ userId }: AssignRoleDrawerProps) => {
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({
    [CREATE_FORM_ID]: false,
  });

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const setIsDirtyHandler = useCallback(
    (formId: string, value: boolean) => setDirtyFields((prev) => ({ ...prev, [formId]: value })),
    []
  );

  const { data, loading } = useQuery(USER_ROLE_BINDINGS_WITH_ROLES, {
    variables: {
      id: userId,
    },
  });

  const assignedRoles = useMemo(
    () =>
      data?.managedUser.userRoleBindings.map((binding) => ({
        id: binding.id,
        roleID: binding.role.id,
        spaceID: binding.space.id,
        spaceName: binding.space.name,
      })) || [],
    [data?.managedUser.userRoleBindings]
  );

  const [createBatchRoleBinding, { loading: createBatchRoleBindingLoading }] = useMutation(
    USER_ROLE_BINDING_BATCH_CREATE,
    {
      refetchQueries: ["UserRoleBindingsWithRoles"],
      awaitRefetchQueries: true,
    }
  );

  const onCreate = useCallback(
    (formData: AssignRoleFormAddFields) => {
      const roleBindings = formData.roleIds.map((roleId) => ({
        roleID: roleId,
        spaceID: formData.space,
        userID: userId,
      }));

      return createBatchRoleBinding({
        variables: {
          input: {
            bindings: roleBindings,
          },
        },
      })
        .then(() => {
          reportSuccess({
            message: `${pluralize("Role", roleBindings.length)} successfully assigned`,
          });
        })
        .catch(onError);
    },
    [createBatchRoleBinding, onError, reportSuccess, userId]
  );

  const [updateRoleBinding] = useMutation(USER_ROLE_BINDING_UPDATE, {
    refetchQueries: ["UserRoleBindingsWithRoles"],
    awaitRefetchQueries: true,
  });

  const onEdit = useCallback(
    (formData: AssignRoleFormEditFields, roleAssignmentId: string) => {
      updateRoleBinding({
        variables: {
          id: roleAssignmentId,
          input: {
            roleID: formData.roleId,
            spaceID: formData.space,
            userID: userId,
          },
        },
      })
        .then(() => {
          reportSuccess({ message: "Role assignment successfully updated" });
          setIsDirtyHandler(roleAssignmentId, false);
        })
        .catch(onError);
    },
    [updateRoleBinding, onError, reportSuccess, setIsDirtyHandler, userId]
  );

  const availableRoles = useMemo(() => data?.roles || [], [data?.roles]);
  const isDirty = useMemo(() => !!Object.values(dirtyFields).filter(Boolean).length, [dirtyFields]);

  const onDelete = useCallback(
    (roleAssignmentId: string, roleName: string) =>
      showUnassignConfirmation({
        id: roleAssignmentId,
        name: roleName,
        isLastRole: assignedRoles.length === 1,
      }),
    [assignedRoles.length]
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Manage roles" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody>
        <Box direction="column" gap="large">
          <AssignRoleForm
            onCreate={onCreate}
            onEdit={onEdit}
            setDirtyFields={setIsDirtyHandler}
            availableRoles={availableRoles}
            assignedRoles={assignedRoles}
            loading={loading}
            createLoading={createBatchRoleBindingLoading}
            onDelete={onDelete}
          />
        </Box>
      </DrawerBody>
    </DrawerForm>
  );
});

export const showAssignRoleDrawer = createDrawerTrigger(AssignRoleDrawer);
